import { useCallback, useEffect, useRef, useState } from "react";

import { api_without_cancellation } from "../../../../api/api";

const BASE_URL = "/api/firmsetting-page/case-merge/";

export const getMergeError = (error) =>
  error?.response?.data?.error || "Unable to complete the request. Please try again.";

export const useCaseMergeClients = (searchQuery) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const requestId = useRef(0);

  useEffect(() => {
    const id = ++requestId.current;
    const query = searchQuery.trim();
    setData([]);
    setError("");
    setLoading(Boolean(query));
    if (!query) return undefined;

    const timer = window.setTimeout(async () => {
      try {
        const response = await api_without_cancellation.get(`${BASE_URL}clients/`, {
          params: { search: query },
        });
        if (id === requestId.current) {
          setData(Array.isArray(response.data) ? response.data : []);
        }
      } catch (error) {
        if (id === requestId.current) setError(getMergeError(error));
      } finally {
        if (id === requestId.current) setLoading(false);
      }
    }, 300);

    return () => {
      window.clearTimeout(timer);
      requestId.current += 1;
    };
  }, [searchQuery]);

  return { data, loading, error };
};

const useCaseMerge = (clientId, onMerged) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [targetId, setTargetId] = useState(null);
  const [sourceIds, setSourceIds] = useState([]);
  const [confirmation, setConfirmation] = useState(null);
  const [operation, setOperation] = useState("");
  const requestId = useRef(0);
  const operationLock = useRef(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      requestId.current += 1;
    };
  }, []);

  const loadClient = useCallback(async (id, preserveError = false) => {
    const request = ++requestId.current;
    setData(null);
    setTargetId(null);
    setSourceIds([]);
    setConfirmation(null);
    if (!preserveError) setError("");
    setLoading(Boolean(id));
    if (!id) return;

    try {
      const response = await api_without_cancellation.get(`${BASE_URL}clients/${id}/`);
      if (mounted.current && request === requestId.current) setData(response.data);
    } catch (error) {
      if (mounted.current && request === requestId.current) setError(getMergeError(error));
    } finally {
      if (mounted.current && request === requestId.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadClient(clientId);
  }, [clientId, loadClient]);

  const selectTarget = (id) => {
    if (operationLock.current) return;
    setTargetId(id);
    setSourceIds((ids) => ids.filter((source) => source !== id));
    setConfirmation(null);
    setError("");
  };

  const toggleSource = (id) => {
    if (operationLock.current || id === targetId) return;
    setSourceIds((ids) =>
      ids.includes(id) ? ids.filter((source) => source !== id) : [...ids, id]
    );
    setConfirmation(null);
    setError("");
  };

  const recoverFromError = async (error) => {
    if (!mounted.current) return;
    setError(getMergeError(error));
    setConfirmation(null);
    if ([404, 409].includes(error?.response?.status)) await loadClient(clientId, true);
  };

  const canPreview = Boolean(data && targetId && sourceIds.length && !loading && !operation);

  const previewMerge = async () => {
    if (!canPreview || operationLock.current) return;
    operationLock.current = true;
    setOperation("preview");
    setError("");
    // Keep the exact previewed selection for the eventual destructive request.
    const payload = {
      client_id: Number(clientId),
      target_case_id: Number(targetId),
      source_case_ids: sourceIds.map(Number),
    };
    try {
      const response = await api_without_cancellation.post(`${BASE_URL}preview/`, payload);
      if (mounted.current) setConfirmation({ data: response.data, payload });
    } catch (error) {
      await recoverFromError(error);
    } finally {
      operationLock.current = false;
      if (mounted.current) setOperation("");
    }
  };

  const mergeCases = async () => {
    if (!confirmation || operationLock.current) return;
    operationLock.current = true;
    setOperation("merge");
    setError("");
    let response;
    try {
      response = await api_without_cancellation.post(BASE_URL, confirmation.payload);
    } catch (error) {
      await recoverFromError(error);
      operationLock.current = false;
      if (mounted.current) setOperation("");
      return;
    }

    // A refresh failure must never be presented as a failed merge or invite a retry.
    const payload = confirmation.payload;
    if (mounted.current) {
      setConfirmation(null);
      setSourceIds([]);
    }
    await Promise.allSettled([
      mounted.current ? loadClient(clientId) : Promise.resolve(),
      Promise.resolve().then(() => onMerged(response.data, payload)),
    ]);
    operationLock.current = false;
    if (mounted.current) setOperation("");
  };

  return {
    data,
    loading,
    error,
    targetId,
    sourceIds,
    confirmation,
    operation,
    canPreview,
    selectTarget,
    toggleSource,
    previewMerge,
    mergeCases,
    closeConfirmation: () => {
      if (!operationLock.current) setConfirmation(null);
    },
    retry: () => loadClient(clientId),
  };
};

export default useCaseMerge;
